'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

export default function AcceptableUsePolicyPage() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Acceptable Use Policy</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Guidelines for responsible and secure use of our services
            </motion.p>
          </div>
        </div>
      </section>

      {/* Acceptable Use Policy Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300 mb-4">Effective Date: 01 Nov, 2024</p>
              <p className="text-slate-300">This Acceptable Use Policy ("AUP") outlines the rules and guidelines for the use of services provided by 1 Dollar Cloud, a service of Global Ads Media, LLC. By signing up and using our hosting services, you agree to comply with this policy. Failure to comply with these guidelines may result in the suspension or termination of your account.</p>
            </div>

            {[
              {
                title: "1. Purpose of Service",
                content: [
                  "1 Dollar Cloud provides hosting services to allow individuals and businesses to store their data and host websites securely. Our hosting services are designed to support lawful, professional, and responsible use by our customers."
                ]
              },
              {
                title: "2. Acceptable Use of Services",
                content: [
                  "When you sign up for our hosting services, either through email or third-party services (Google, Facebook, or Twitter), and provide your personal details (including but not limited to name, email address, phone number, and possibly an ID for identity verification), you agree to the following terms:",
                  "• You will use our services for lawful purposes only.",
                  "• You will comply with all applicable local, state, and international laws, including data protection and privacy laws.",
                  "• You will not use our services to store, transmit, or engage in any activity that violates the rights of others."
                ]
              },
              {
                title: "3. Prohibited Activities",
                content: [
                  "The following actions are strictly prohibited when using 1 Dollar Cloud hosting services:",
                  "Illegal Activities: Using our services to store or transmit any content related to illegal activities, including but not limited to fraud, hacking, identity theft, pirated software, child exploitation, or gambling, is strictly prohibited.",
                  "Malware and Viruses: You may not upload, store, or transmit any malware, viruses, worms, or other harmful code that may damage, disrupt, or impair the functionality of our servers or services.",
                  "Spamming and Phishing: Engaging in or facilitating any form of spamming (e.g., unsolicited commercial emails) or phishing (e.g., collecting personal information under false pretenses) is prohibited.",
                  "Offensive Content: Hosting, transmitting, or distributing any content that is obscene, offensive, defamatory, discriminatory, or violates others' privacy or intellectual property rights is not allowed.",
                  "Overloading Servers: You may not engage in actions that disrupt or excessively burden our servers, network infrastructure, or other customers, including sending excessive traffic or launching denial-of-service (DoS) attacks.",
                  "Unauthorized Access: Attempting to gain unauthorized access to any system, account, or network associated with 1 Dollar Cloud or its customers is prohibited. This includes attempts to bypass security measures or obtain confidential information.",
                  "Violation of Intellectual Property: Uploading or distributing copyrighted material without permission or violating the intellectual property rights of others is not allowed."
                ]
              },
              {
                title: "4. User Data and Privacy",
                content: [
                  "When you purchase hosting services, you may store personal or business information on our servers. You are responsible for ensuring that the data you upload is lawful and complies with data protection laws.",
                  "1 Dollar Cloud respects your privacy and handles your personal and business data according to our Privacy Policy. However, you are responsible for securing your data, including backups, as we cannot guarantee full data recovery in case of data loss.",
                  "We may access your data only for troubleshooting, support, or legal compliance purposes, and we will not share or disclose your personal data without your consent, except as required by law."
                ]
              },
              {
                title: "5. Prohibited Use of Customer Information",
                content: [
                  "You agree not to use any information, data, or content stored in our systems for any unauthorized purpose, such as sharing or selling personal, sensitive, or financial data without proper consent."
                ]
              },
              {
                title: "6. Account Management and Security",
                content: [
                  "You are responsible for maintaining the confidentiality of your account credentials and ensuring the security of your hosting environment. This includes safeguarding your username, password, and any other access credentials.",
                  "You must notify us immediately if you become aware of any unauthorized use of your account or any security breach related to your hosting services."
                ]
              },
              {
                title: "7. Suspension or Termination of Service",
                content: [
                  "1 Dollar Cloud reserves the right to suspend or terminate your hosting services without notice if we determine that you have violated any part of this Acceptable Use Policy.",
                  "In the event of service termination, we may retain or delete your data, depending on the circumstances and in accordance with our Privacy Policy. You will be responsible for downloading any important data before termination."
                ]
              },
              {
                title: "8. Refund Policy",
                content: [
                  "1 Dollar Cloud offers a 7-days refund policy for new hosting service purchases. After 7 days, no refunds will be provided. This refund policy does not apply to domain registrations or third-party services. For full details, please refer to our Refund Policy."
                ]
              },
              {
                title: "9. Modifications to the AUP",
                content: [
                  "1 Dollar Cloud reserves the right to modify or update this Acceptable Use Policy at any time. Any changes will be effective immediately upon posting on our website. We will notify you of any significant updates through email or notifications. It is your responsibility to review this policy periodically."
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4">{paragraph}</p>
                ))}
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-800/50 rounded-xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">10. Contact Information</h2>
              <p className="text-slate-300 mb-4">If you have any questions or concerns regarding this Acceptable Use Policy, or if you need support, please contact us at:</p>
              <div className="text-slate-300">
                <p className="font-semibold">1 Dollar Cloud</p>
                <p>a service of Global Ads Media, LLC.</p>
                <p>66 West Flagler Street Miami Florida 33130 USA</p>
                <p className="mt-4">Email:</p>
                <p>info@1dollarcloud.com</p>
                <p>info@globaladsmedia.us</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-slate-300 text-center mt-8"
            >
              By using our services, you confirm your agreement with this Acceptable Use Policy and all associated terms.
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}